import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Loader2, CheckCircle2, XCircle, Gauge, LogIn } from "lucide-react";
import apiClient, { getErrorMessage } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import logo from "@/assets/logo.png";

export default function Pair() {
  const { code } = useParams();
  const { user, loading: authLoading } = useAuth();
  const [station, setStation] = useState(null);
  const [state, setState] = useState("loading"); // loading | invalid | need-login | pairing | done | error
  const [error, setError] = useState("");

  useEffect(() => {
    apiClient.get(`/pair/${code}`)
      .then(({ data }) => setStation(data))
      .catch(() => setState("invalid"));
  }, [code]);

  useEffect(() => {
    if (state === "invalid" || !station) return;
    if (authLoading) return;
    if (!user) { setState("need-login"); return; }
    setState("pairing");
    apiClient.post("/simhub/pair", { code })
      .then(() => setState("done"))
      .catch((err) => { setError(getErrorMessage(err, "Pairing failed")); setState("error"); });
  }, [station, user, authLoading, state, code]);

  return (
    <div className="min-h-screen flex items-center justify-center p-6" style={{ background: "radial-gradient(1200px 600px at 50% -10%, rgba(0,229,255,0.12), transparent), #06080f" }}>
      <div className="glass rounded-3xl p-8 max-w-md w-full text-center" data-testid="pair-card">
        <img src={logo} alt="Pit Lane" className="h-12 mx-auto mb-6" />
        <div className="mx-auto mb-5 w-16 h-16 rounded-2xl flex items-center justify-center" style={{ background: "rgba(0,229,255,0.12)", border: "1px solid rgba(0,229,255,0.4)" }}>
          <Gauge className="w-8 h-8" style={{ color: "#00e5ff" }} />
        </div>

        {(state === "loading" || state === "pairing") && (
          <div data-testid="pair-loading"><Loader2 className="w-6 h-6 animate-spin mx-auto text-cyan-300" /><p className="text-slate-400 mt-3">{state === "pairing" ? "Pairing your session..." : "Loading station..."}</p></div>
        )}

        {state === "invalid" && (
          <div data-testid="pair-invalid">
            <XCircle className="w-10 h-10 text-pink-400 mx-auto" />
            <h1 className="font-display text-2xl font-bold text-white mt-3">Invalid QR code</h1>
            <p className="text-slate-400 mt-2">This station's pairing code is inactive or doesn't exist. Ask a staff member for help.</p>
          </div>
        )}

        {state === "need-login" && station && (
          <div data-testid="pair-need-login">
            <h1 className="font-display text-2xl font-bold text-white">Pair with {station.name}</h1>
            <p className="text-slate-400 mt-2 mb-6">Log in (or create a free account) so your lap times and session data are saved to your profile.</p>
            <Link to="/login" state={{ from: `/pair/${code}` }} className="neon-btn w-full py-3 mb-3" data-testid="pair-login-btn"><LogIn className="w-4 h-4" /> Log in to pair</Link>
            <Link to="/register" state={{ from: `/pair/${code}` }} className="neon-outline-btn w-full py-3" data-testid="pair-register-btn">Create an account</Link>
          </div>
        )}

        {state === "done" && station && (
          <div data-testid="pair-done">
            <CheckCircle2 className="w-10 h-10 mx-auto" style={{ color: "#00ff9d" }} />
            <h1 className="font-display text-2xl font-bold text-white mt-3">You're paired! 🏁</h1>
            <p className="text-slate-300 mt-2">Session data from <b>{station.name}</b> will now be saved to your profile. Jump in and drive — check your stats afterwards.</p>
            <Link to="/sessions" className="neon-btn w-full py-3 mt-6" data-testid="pair-view-sessions">View my sessions</Link>
          </div>
        )}

        {state === "error" && (
          <div data-testid="pair-error">
            <XCircle className="w-10 h-10 text-pink-400 mx-auto" />
            <h1 className="font-display text-2xl font-bold text-white mt-3">Couldn't pair</h1>
            <p className="text-slate-400 mt-2">{error}</p>
          </div>
        )}
      </div>
    </div>
  );
}
